// src/pages/admin/InterviewManagement.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import {
  VideoCameraIcon,
  PlusIcon,
  EyeIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import CreateInterviewModal from './CreateInterviewModal';
// import ScheduleInterviewModal from './ScheduleInterviewModal';
import InterviewDetailsModal from './InterviewDetailsModal';
import EditInterviewQuestionsModal from '../../components/admin/EditInterviewQuestionsModal';

const InterviewManagement = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  // const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [editingInterview, setEditingInterview] = useState(null);
  // const [selectedInterviewForSchedule, setSelectedInterviewForSchedule] = useState(null);
  const queryClient = useQueryClient();

  // Fetch interviews
  const { data: interviewsData, isLoading } = useQuery({
    queryKey: ['admin-interviews'],
    queryFn: () => apiService.getInterviews(),
  });

  // Fetch job descriptions
  const { data: jobsData } = useQuery({
    queryKey: ['job-descriptions'],
    queryFn: () => apiService.getJobDescriptions(),
  });

  // Fetch users for scheduling
  // const { data: usersData } = useQuery({
  //   queryKey: ['users-candidates'],
  //   queryFn: () => apiService.getUsers(1, 100, { role: 'candidate' }),
  // });

  // Create interview mutation
  const createInterviewMutation = useMutation({
    mutationFn: (data) => apiService.createInterview(data),
    onSuccess: () => {
      toast.success('Interview created successfully!');
      setShowCreateModal(false);
      queryClient.invalidateQueries(['admin-interviews']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create interview');
    },
  });

  // Schedule interview mutation
  // const scheduleInterviewMutation = useMutation({
  //   mutationFn: ({ interviewId, candidateIds }) => 
  //     apiService.scheduleInterview(interviewId, candidateIds),
  //   onSuccess: () => {
  //     toast.success('Interview scheduled successfully!');
  //     setShowScheduleModal(false);
  //     setSelectedInterviewForSchedule(null);
  //     queryClient.invalidateQueries(['admin-interviews']);
  //   },
  //   onError: (error) => {
  //     toast.error(error.message || 'Failed to schedule interview');
  //   },
  // });

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getQuestionTypeDisplay = (interview) => {
    const aiCount = interview.ai_questions_count || 0;
    const customCount = interview.custom_questions_count || 0;
    const totalCount = interview.total_questions || 0;

    if (interview.custom_questions_only) {
      return {
        badge: 'Custom Only',
        badgeColor: 'bg-purple-100 text-purple-800',
        description: `${customCount} Custom questions`
      };
    } else if (aiCount > 0 && customCount > 0) {
      return {
        badge: 'AI + Custom',
        badgeColor: 'bg-blue-100 text-blue-800',
        description: `${aiCount} AI, ${customCount} Custom`
      };
    } else if (aiCount > 0) {
      return {
        badge: 'AI Only',
        badgeColor: 'bg-green-100 text-green-800',
        description: `${aiCount} AI questions`
      };
    } else if (customCount > 0) {
      return {
        badge: 'Custom Only',
        badgeColor: 'bg-purple-100 text-purple-800',
        description: `${customCount} Custom questions`
      };
    } else {
      return {
        badge: 'No Questions',
        badgeColor: 'bg-red-100 text-red-800',
        description: 'No questions configured'
      };
    }
  };

  // const handleScheduleInterview = (interview) => {
  //   setSelectedInterviewForSchedule(interview);
  //   setShowScheduleModal(true);
  // };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Interview Management</h1>
          <p className="text-gray-600">Create and manage interviews for candidates.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Interview
        </button>
      </div>

      {/* Interviews List */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : interviewsData?.interviews?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Interview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Questions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {interviewsData.interviews.map((interview) => {
                  const questionDisplay = getQuestionTypeDisplay(interview);
                  
                  return (
                    <tr key={interview.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <VideoCameraIcon className="h-8 w-8 text-primary-600 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {interview.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {interview.duration_minutes} minutes
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${questionDisplay.badgeColor}`}>
                            {questionDisplay.badge}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {questionDisplay.description}
                        </div>
                        <div className="text-xs text-gray-400">
                          Total: {interview.total_questions || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(interview.status)}`}>
                          {interview.status?.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {interview.session_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(interview.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/admin/interviews/${interview.id}/results`}
                            className="text-primary-600 hover:text-primary-900"
                            title="View Results"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          {/* <button
                            onClick={() => handleScheduleInterview(interview)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Schedule for Candidates"
                          >
                            <UserGroupIcon className="h-4 w-4" />
                          </button> */}
                          {interview.session_count === 0 ? (
                            <button
                              onClick={() => setEditingInterview(interview)}
                              className="text-gray-600 hover:text-gray-900"
                              title="Edit Questions"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              className="text-gray-400 cursor-not-allowed"
                              title="Cannot edit - Interview has sessions"
                              disabled
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <VideoCameraIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No interviews found</p>
            <p className="text-sm text-gray-400">Create your first interview to get started</p>
          </div>
        )}
      </div>

      {/* Create Interview Modal */}
      {showCreateModal && (
        <CreateInterviewModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createInterviewMutation.mutate(data)}
          jobDescriptions={jobsData?.job_descriptions || []}
          isLoading={createInterviewMutation.isLoading}
        />
      )}

      {/* Schedule Interview Modal */}
      {/* {showScheduleModal && selectedInterviewForSchedule && (
        <ScheduleInterviewModal
          interview={selectedInterviewForSchedule}
          candidates={usersData?.users || []}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedInterviewForSchedule(null);
          }}
          onSubmit={(candidateIds) => 
            scheduleInterviewMutation.mutate({
              interviewId: selectedInterviewForSchedule.id,
              candidateIds
            })
          }
          isLoading={scheduleInterviewMutation.isLoading}
        />
      )} */}

      {/* Interview Details Modal */}
      {selectedInterview && (
        <InterviewDetailsModal
          interview={selectedInterview}
          onClose={() => setSelectedInterview(null)}
        />
      )}

      {/* Edit Interview Questions Modal */}
      {editingInterview && (
        <EditInterviewQuestionsModal
          interview={editingInterview}
          onClose={() => {
            setEditingInterview(null);
            queryClient.invalidateQueries(['admin-interviews']);
          }}
        />
      )}
    </div>
  );
};

export default InterviewManagement;